/**
 * Journey Service
 *
 * Journey API methods for creating, fetching, and managing journey instances.
 */

import type {
  InvitationKind,
  JourneyArchiveBlock,
  JourneyCompany,
  JourneyCompanyRelationship,
  JourneyProgressSummary,
} from '@tonnie/types';
import type {
  JourneyCreateOutcome,
  JourneyRouteAdvice,
  JourneyTierBadge,
} from '@tonnie/foundation';
import { TonnieClient } from '../client/base';
import { API_ENDPOINTS } from '../client/config';

// ═══════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════

/**
 * Funding source — the money axis of a journey. Mirrors the
 * `journey_funding_source` Postgres enum and is the canonical signal
 * for plan-gating decisions on the frontend.
 */
export type JourneyFundingSource =
  | 'self_free'
  | 'self_paid'
  | 'self_pro'
  | 'invited_only'
  | 'sponsored_b2b'
  | 'sponsored_gov'
  | 'family_shared';

/**
 * Journey lifecycle — where the journey IS in its arc. Mirrors the
 * `journey_lifecycle_state` Postgres enum.
 */
export type JourneyLifecycleState =
  'draft' | 'pending_setup' | 'active' | 'paused' | 'cancelled' | 'completed' | 'expired';

/**
 * One household member as a JOURNEY payload carries them. (W8.D · H6)
 *
 * The composition and nothing else: who the person is to the citizen
 * (`relation`, `type`) and what to call them. It is not a roster DTO — the
 * platform served the whole `household_members` row here, including
 * `legalFullName`, `dateOfBirth`, `nationality`, `email`, `phone`,
 * `passportNumber` and `visaStatus`, for children and partners who never
 * contracted with us. `GET /journeys/:id` and `GET /journeys/current` render
 * none of that; they carry the roster so a client can say who is coming along.
 *
 * A screen that genuinely needs the per-member visa detail reads TelePortal's
 * own household route, which is the roster surface and is ownership-checked in
 * its own right.
 *
 * Declared here, rather than left undeclared as it was, because an undeclared
 * field on a response is a field every consumer has to cast for — and a cast is
 * how a shape nobody agreed to gets depended on.
 */
export interface JourneyHouseholdMember {
  id: string;
  /** Relation to the citizen, e.g. `spouse`, `child`. */
  relation: string;
  type: string | null;
  firstName: string | null;
  lastName: string | null;
}

export interface Journey {
  id: string;
  userId: string;
  status: 'not_started' | 'in_progress' | 'completed';
  startedAt?: string;
  completedAt?: string;
  currentPhase: string;
  progress: number;
  meshScores?: MeshScores;
  milesEarned: number;
  createdAt: string;
  updatedAt: string;
  // Journey Instance fields
  originCountry?: string | null;
  destinationCountry?: string;
  /**
   * The citizen's name for this journey (D-F) — what tells two journeys on the
   * same route apart. `null` = the card derives its line (company or scenario
   * and the start date). List reads only.
   */
  label?: string | null;
  scenario?: string;
  /**
   * Cornerstone refactor 2026-05: replaced the legacy `unlockMode` +
   * `accessLevel` + `pricingPlan` trio with a single canonical
   * `fundingSource` enum. Tier is derived (see @tonnie/foundation
   * `tierOfFunding`).
   */
  fundingSource?: JourneyFundingSource;
  lifecycleState?: JourneyLifecycleState;
  householdConfig?: Record<string, unknown> | null;
  heroesEnabled?: string[];
  programId?: string | null;
  cohortId?: string | null;
  activatedAt?: string | null;
  expiresAt?: string | null;
  isPrimary?: boolean;
  /**
   * What the companies on the journey ask its citizen for — `full_checkup`
   * when any of them asks the full CheckUp, `testit` when every one asks only
   * for tests, `null` when no company's invitation is in play. Derived by the
   * server from the journey's live relationships (D3); the first-run resolver
   * reads it. Each company's own ask is `companies[].invitationKind`.
   */
  invitationKind?: InvitationKind | null;
  /**
   * Every company on the journey, earliest relationship first — each with its
   * own name, logo, relationship state and tenant (what "leave this company's
   * programme" is addressed to). Two companies that invite the citizen to the
   * same destination share one journey (D-19), so a journey can have several;
   * a self-built one has none.
   *
   * Optional because only the journey LIST (and `current`, drawn from it)
   * carries it.
   */
  companies?: JourneyCompany[];
  /**
   * The card's access badges — tier, funding kind, and whether the company
   * name is an invitation attribution — computed by the API through
   * `journeyTierBadge` in @tonnie/foundation. (F47)
   *
   * Clients render it as it comes and derive nothing from `fundingSource`.
   * Optional because only the journey LIST (and `current`, which is drawn from
   * it) carries it.
   */
  tierBadge?: JourneyTierBadge;
  /**
   * ISO date a renewal made NOW would extend access to — computed by the API
   * with the same rule the renewal writes (L6 round 3). Clients show it; they
   * never add the renewal period themselves. `null` when the talent may not
   * renew the journey (a company still sponsors it): clients offer Renew only
   * when it is present. List reads only.
   */
  renewalExpiresAt?: string | null;
  /**
   * Why this journey cannot be archived yet — a live programme, or a running
   * sponsorship and the end of its paid term — or `null` when it can. The
   * server's answer (the rule its archive refusal applies); the card draws it.
   * List reads only.
   */
  archiveBlock?: JourneyArchiveBlock | null;
  /**
   * The citizen's household, on the two reads that carry one:
   * `GET /journeys/:id` and `GET /journeys/current`. Absent from `GET
   * /journeys` (the list), which is why it is optional.
   */
  familyMembers?: JourneyHouseholdMember[];
}

/** One archived journey, as the citizen's "Arquivadas" section reads it. */
export interface ArchivedJourney {
  id: string;
  tenantId: string;
  talentId: string;
  originCountry?: string | null;
  destinationCountry: string;
  /** The citizen's name for it (D-F). */
  label: string | null;
  lifecycleState: JourneyLifecycleState;
  fundingSource: JourneyFundingSource;
  evaluationPersona?: string | null;
  activatedAt?: string | null;
  expiresAt?: string | null;
  createdAt: string;
  /** When the CITIZEN archived it. */
  archivedAt: string;
}

export interface ArchivedJourneyList {
  journeys: ArchivedJourney[];
  total: number;
}

export interface CitizenArchiveResult {
  journeyId: string;
  /** The journey promoted to primary in the same operation, if any. */
  promotedJourneyId: string | null;
  archivedCount: number;
}

export interface CitizenRestoreResult {
  journeyId: string;
  primaryJourneyId: string | null;
  archivedCount: number;
}

/** What choosing a journey returns. (F47 · D-5) */
export interface CitizenSelectResult {
  journeyId: string;
  /** The citizen's primary journey after the call — always `journeyId`. */
  primaryJourneyId: string;
  /** False when it was already primary: nothing was written. */
  changed: boolean;
}

/**
 * What leaving a programme returns. Leaving ENDS the relationship (D1), so the
 * journey can usually be archived next; it cannot only while ANOTHER company's
 * programme is live on it or a sponsorship runs, and the reason says which.
 */
export type LeaveProgrammeResult = {
  journeyId: string;
  companyTenantId: string;
  /** When the relationship ended. A repeat leave reports the first. */
  leftAt: string | null;
} & (
  | { canArchive: true; archiveBlockedReason: null }
  | { canArchive: false; archiveBlockedReason: 'live_company_program' | 'active_sponsorship' }
);

// ── Per-scope consent (W8.D) ───────────────────────────────────────────
//
// The citizen's answer to "who holds what of mine, and can I take it back".
// `LeaveProgrammeResult` above is the coarser act: leaving revokes EVERY scope
// one company holds AND ends its relationship. These carry the scopes one at a
// time, on a relationship that continues.

/**
 * A consent scope name, as the platform's vocabulary spells it.
 *
 * Deliberately a plain `string` here rather than a union imported from
 * `@tonnie/foundation`: the server validates the vocabulary at the edge
 * (`isConsentScope` → 422), and a client union would go stale the first time a
 * new hero becomes consentable — the `text[]` column exists precisely so that
 * needs no migration and no client release. The screen renders each scope
 * through an i18n key built from the name, so an unknown one renders its raw
 * key LOUDLY rather than disappearing.
 */
export type ConsentScopeName = string;

/** One change on the append-only consent trail. */
export interface ConsentTrailEntry {
  at: string;
  /** `granted` | `revoked` | `widened` | `narrowed`, derived by the server. */
  action: string;
  /** The FULL resulting set at that moment, never a delta. */
  scopes: ConsentScopeName[];
  source: string;
  note: string | null;
}

/** One company's relationship with this journey, as the citizen reads it. */
export interface JourneyConsentRelationship {
  engagementId: string;
  tenantId: string;
  companyName: string;
  journeyInstanceId: string;
  scopes: ConsentScopeName[];
  grantedAt: string | null;
  revokedAt: string | null;
  source: string | null;
  textVersion: string | null;
  /**
   * The relationship's state — the same one the journey card's badge shows.
   * `archived_by_company`: the company filed it away and can STILL read.
   * `left` / `ended`: it is over, and nothing can be shared with it again
   * (409 `CONSENT_RELATIONSHIP_ENDED`) — the way back is a new invitation.
   */
  relationship: JourneyCompanyRelationship;
  /** When the relationship ended (`left` / `ended`), else null. */
  endedAt: string | null;
  /** Oldest first. */
  trail: ConsentTrailEntry[];
  /**
   * Every scope this company has ever held on this relationship, in vocabulary
   * order — what the citizen's screen lists, each switched on or off by whether
   * it is in `scopes`. A withdrawn scope stays listed so it can be given back;
   * one the company never held is never offered.
   */
  grantableScopes: ConsentScopeName[];
}

export interface JourneyConsentDetail {
  journeyId: string;
  relationships: JourneyConsentRelationship[];
}

/** What `PUT :id/consents/:tenantId` returns: the new head, as stored. */
export interface JourneyConsentUpdateResult {
  engagementId: string;
  tenantId: string;
  journeyInstanceId: string;
  scopes: ConsentScopeName[];
  grantedAt: string | null;
  revokedAt: string | null;
  source: string | null;
  textVersion: string | null;
}

export interface MeshScores {
  mindset: number;
  execution: number;
  sustainability: number;
  health: number;
  overall: number;
}

/**
 * Input for creating a new journey instance.
 * Maps to the platform-api POST /api/journeys endpoint.
 *
 * Cornerstone refactor 2026-05: clients no longer negotiate funding
 * source / tier at create time. Self-signup journeys always start as
 * `funding_source='self_free'` on the backend; the billing-webhook
 * auto-upgrade flips them to `self_pro` if the user has Pro.
 *
 * `originCountry` is now nullable — pending_setup journeys (e.g. an
 * accepted invitation that hasn't gone through onboarding) leave it
 * `null` instead of using the legacy 'UNK' sentinel.
 */
export interface CreateJourneyInput {
  originCountry: string | null;
  destinationCountry: string;
  scenario: string;
  householdConfig?: {
    familySize?: number;
    hasSpouse?: boolean;
    hasChildren?: boolean;
    childrenCount?: number;
    childrenAges?: number[];
  };
  heroesEnabled?: string[];
  programId?: string;
  cohortId?: string;
  /**
   * `'new'` — the citizen's "Build" / "Build another": always a new journey,
   * idempotent on `requestId` and capped per route (409
   * `JOURNEY_ROUTE_CAP_REACHED`). Every client in this repository sends it;
   * the legacy no-intent request is the installed store builds'.
   */
  intent?: 'new';
  /** One id per create ATTEMPT, kept across its retries (R3). Required with `intent: 'new'`. */
  requestId?: string;
  /** The citizen's name for the new journey (D-F), ≤ 40 characters. */
  label?: string;
}

/** What `POST /journeys` answers: the journey, and what happened (R10). */
export type CreatedJourney = Journey & { outcome: JourneyCreateOutcome };

/** `GET /journeys/route-advice` — the server's verdict on a route, with the journeys behind it (R4). */
export type JourneyRouteAdviceResult = JourneyRouteAdvice<Journey>;

// ═══════════════════════════════════════════════════════════════════════
// SERVICE
// ═══════════════════════════════════════════════════════════════════════

export class JourneyService {
  constructor(private readonly client: TonnieClient) {}

  /** Get all journeys for current user */
  async getJourneys(): Promise<Journey[]> {
    return this.client.get<Journey[]>(API_ENDPOINTS.JOURNEYS.BASE);
  }

  /** Get journey by ID */
  async getJourney(id: string): Promise<Journey> {
    return this.client.get<Journey>(API_ENDPOINTS.JOURNEYS.BY_ID(id));
  }

  /** The journey's progress through each hero — every number the server's. */
  async getJourneyProgress(id: string): Promise<JourneyProgressSummary> {
    return this.client.get<JourneyProgressSummary>(API_ENDPOINTS.JOURNEYS.PROGRESS(id));
  }

  /** Get current active journey */
  async getCurrentJourney(): Promise<Journey | null> {
    try {
      return await this.client.get<Journey>(API_ENDPOINTS.JOURNEYS.CURRENT);
    } catch (error) {
      return null;
    }
  }

  /** Create a new journey instance with full configuration */
  async createJourney(input: CreateJourneyInput): Promise<CreatedJourney> {
    return this.client.post<CreatedJourney>(API_ENDPOINTS.JOURNEYS.BASE, input);
  }

  /** What the citizen already holds on `origin → destination`, before they build it (R4). */
  async getRouteAdvice(origin: string, destination: string): Promise<JourneyRouteAdviceResult> {
    return this.client.get<JourneyRouteAdviceResult>(API_ENDPOINTS.JOURNEYS.ROUTE_ADVICE, {
      params: { origin, destination },
    });
  }

  /** Name a journey, or clear its name with `null` (D-F). */
  async setJourneyLabel(
    id: string,
    label: string | null
  ): Promise<{ journeyId: string; label: string | null }> {
    return this.client.put<{ journeyId: string; label: string | null }>(
      API_ENDPOINTS.JOURNEYS.LABEL(id),
      { label }
    );
  }

  // ── Citizen journey management (W8.1 · F47) ───────────────────────────

  /** The journeys the citizen has archived themselves. */
  async getArchivedJourneys(): Promise<ArchivedJourneyList> {
    return this.client.get<ArchivedJourneyList>(API_ENDPOINTS.JOURNEYS.ARCHIVED);
  }

  /**
   * How many journeys the citizen has archived.
   *
   * Its own endpoint because the zero-journey empty state needs the number at a
   * moment when there is no journey to hang it off — which is the case D-29
   * turns on: archiving the LAST journey is allowed precisely because the empty
   * state can then offer "recover an archived journey" beside "build one".
   */
  async getArchivedJourneyCount(): Promise<{ archivedCount: number }> {
    return this.client.get<{ archivedCount: number }>(API_ENDPOINTS.JOURNEYS.ARCHIVED_COUNT);
  }

  /** Archive a journey. 409 `CITIZEN_JOURNEY_ARCHIVE_BLOCKED` while a company holds it. */
  async archiveJourney(id: string): Promise<CitizenArchiveResult> {
    return this.client.post<CitizenArchiveResult>(API_ENDPOINTS.JOURNEYS.ARCHIVE(id), {});
  }

  /** Restore a journey the citizen archived. 409 `JOURNEY_ROUTE_CAP_REACHED` when its route is full (D-A). */
  async restoreJourney(id: string): Promise<CitizenRestoreResult> {
    return this.client.post<CitizenRestoreResult>(API_ENDPOINTS.JOURNEYS.RESTORE(id), {});
  }

  /**
   * Choose this journey: persists `is_primary`, which hero access,
   * `GET /journeys/current` and the citizen's other device read. (F47 · D-5)
   */
  async selectJourney(id: string): Promise<CitizenSelectResult> {
    return this.client.post<CitizenSelectResult>(API_ENDPOINTS.JOURNEYS.SELECT(id), {});
  }

  /**
   * Leave a company's programme — which ENDS that company's relationship (D1):
   * its read collapses to `{}`, the relationship ends, and the company is told.
   * Nothing is deleted. Leaving twice answers the same and changes nothing.
   */
  async leaveCompanyProgramme(id: string, companyTenantId: string): Promise<LeaveProgrammeResult> {
    return this.client.post<LeaveProgrammeResult>(
      API_ENDPOINTS.JOURNEYS.LEAVE_PROGRAMME(id, companyTenantId),
      {}
    );
  }

  // ── Per-scope consent (W8.D) ──────────────────────────────────────────

  /** Who holds what of this journey, with the trail behind each grant. */
  async getConsentDetail(id: string): Promise<JourneyConsentDetail> {
    return this.client.get<JourneyConsentDetail>(API_ENDPOINTS.JOURNEYS.CONSENTS_DETAIL(id));
  }

  /**
   * Set exactly which scopes ONE company may read on this journey.
   *
   * The FULL resulting set, never a delta: two open tabs must resolve to "last
   * write wins", visibly, rather than composing into a set neither person
   * chose. `[]` withdraws everything while the relationship continues — the
   * company keeps the relationship and reads nothing; leaving the programme
   * is the act that ENDS it.
   *
   * 409 `CONSENT_RELATIONSHIP_ENDED` when the relationship is over: a
   * programme the citizen left is not shared with again.
   *
   * 422 `CONSENT_SCOPE_UNKNOWN` for a scope the platform does not recognise, and 422 `CONSENT_SCOPE_NEVER_HELD`
   * for one this company never held here — a citizen restores what they
   * withdrew (`grantableScopes`), never extends it.
   */
  async setConsentScopes(
    id: string,
    companyTenantId: string,
    scopes: ConsentScopeName[]
  ): Promise<JourneyConsentUpdateResult> {
    return this.client.put<JourneyConsentUpdateResult>(
      API_ENDPOINTS.JOURNEYS.CONSENT_SCOPES(id, companyTenantId),
      { scopes }
    );
  }

  // NOTE: `updateProgress()` was deleted with F48. It PATCHed
  // `/api/journeys/:id/progress` — a route that has never existed on
  // platform-api (the only handler there is `@Get(':id/progress')`), so every
  // call it could ever have made was a 404. Nothing imported it.
}

export function createJourneyService(client: TonnieClient): JourneyService {
  return new JourneyService(client);
}
